import React, { useState, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  FiCpu, FiTool, FiMessageSquare, FiCheckCircle, 
  FiPlay, FiPause, FiAlertCircle, FiSearch,
  FiFileText, FiCode, FiDatabase, FiClock,
  FiList, FiBarChart2
} from 'react-icons/fi';

interface AgentActivity {
  id: string;
  agent_id: string;
  persona_id: string;
  action: string;
  details: any;
  timestamp: string;
  phase_id?: string;
  phase_label?: string;
  duration?: number;
}

interface AgentActivityTimelineProps {
  activities: AgentActivity[];
  getPersonaName?: (personaId: string) => string;
}

// Add viewMode state

export const AgentActivityTimeline: React.FC<AgentActivityTimelineProps> = ({
  activities,
  getPersonaName
}) => {
  const { darkMode } = useTheme();
  const [expandedActivity, setExpandedActivity] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [groupByPersona, setGroupByPersona] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'list' | 'gantt'>('list');
  
  // Helper function to get persona name
  const getDisplayName = (personaId: string) => {
    if (getPersonaName) {
      return getPersonaName(personaId);
    }
    return personaId;
  };
  
  // Get activity icon based on action type
  const getActivityIcon = (activity: AgentActivity) => {
    const iconSize = 18;
    const iconClassName = "flex-shrink-0";
    
    switch (activity.action) {
      case 'start':
        return <FiPlay size={iconSize} className={`${iconClassName} text-green-500`} />;
      case 'thinking':
        return <FiCpu size={iconSize} className={`${iconClassName} text-yellow-500`} />;
      case 'tool_usage':
        // Different icons based on tool type
        if (activity.details?.tool_name?.includes('search')) {
          return <FiSearch size={iconSize} className={`${iconClassName} text-blue-500`} />;
        } else if (activity.details?.tool_name?.includes('code')) {
          return <FiCode size={iconSize} className={`${iconClassName} text-purple-500`} />;
        } else if (activity.details?.tool_name?.includes('data')) {
          return <FiDatabase size={iconSize} className={`${iconClassName} text-indigo-500`} />;
        } else {
          return <FiTool size={iconSize} className={`${iconClassName} text-orange-500`} />;
        }
      case 'message':
        return <FiMessageSquare size={iconSize} className={`${iconClassName} text-blue-500`} />;
      case 'complete':
        return <FiCheckCircle size={iconSize} className={`${iconClassName} text-green-500`} />;
      case 'error':
        return <FiAlertCircle size={iconSize} className={`${iconClassName} text-red-500`} />;
      case 'pause':
        return <FiPause size={iconSize} className={`${iconClassName} text-amber-500`} />;
      case 'artifact_created':
        return <FiFileText size={iconSize} className={`${iconClassName} text-purple-500`} />;
      default:
        return <FiClock size={iconSize} className={`${iconClassName} text-gray-500`} />;
    }
  };
  
  // Get activity background color based on action type
  const getActivityBackground = (activity: AgentActivity, isHovered: boolean = false) => {
    const baseColor = darkMode ? 'bg-gray-700' : 'bg-white';
    const hoverColor = darkMode ? 'bg-gray-600' : 'bg-gray-50';
    
    const bgColor = isHovered ? hoverColor : baseColor;
    
    // Add a subtle colored border based on activity type
    switch (activity.action) {
      case 'start':
        return `${bgColor} border-l-4 border-green-500`;
      case 'thinking':
        return `${bgColor} border-l-4 border-yellow-500`;
      case 'tool_usage':
        return `${bgColor} border-l-4 border-orange-500`;
      case 'message':
        return `${bgColor} border-l-4 border-blue-500`;
      case 'complete':
        return `${bgColor} border-l-4 border-green-500`;
      case 'error':
        return `${bgColor} border-l-4 border-red-500`;
      case 'artifact_created':
        return `${bgColor} border-l-4 border-purple-500`;
      default:
        return `${bgColor} border-l-4 border-gray-500`;
    }
  };
  
  // Get a more descriptive title for the activity
  const getActivityTitle = (activity: AgentActivity) => {
    switch (activity.action) {
      case 'start':
        return 'Started working';
      case 'thinking':
        return activity.details?.thought 
          ? `Thinking: ${activity.details.thought.substring(0, 50)}${activity.details.thought.length > 50 ? '...' : ''}`
          : 'Thinking...';
      case 'tool_usage':
        return `Used tool: ${activity.details?.tool_name || 'Unknown tool'}`;
      case 'message':
        return `Message: ${activity.details?.message?.substring(0, 50)}${activity.details?.message?.length > 50 ? '...' : ''}`;
      case 'complete':
        return 'Completed task';
      case 'error':
        return `Error: ${activity.details?.error || 'Unknown error'}`;
      case 'artifact_created':
        return `Created artifact: ${activity.details?.artifact_name || 'Unnamed artifact'}`;
      default:
        return activity.action.charAt(0).toUpperCase() + activity.action.slice(1);
    }
  };
  
  // Format timestamp nicely
  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return 'Unknown time';
    
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };
  
  // Format duration nicely
  const formatDuration = (duration?: number) => {
    if (!duration) return null;
    
    if (duration < 1) {
      return `${Math.round(duration * 1000)}ms`;
    } else if (duration < 60) {
      return `${duration.toFixed(1)}s`;
    } else {
      const minutes = Math.floor(duration / 60);
      const seconds = Math.round(duration % 60);
      return `${minutes}m ${seconds}s`;
    }
  };
  
  // Group activities by persona or chronologically
  const processActivities = () => {
    // First, filter activities based on filter and search term
    const filteredActivities = activities.filter(activity => {
      // Filter by activity type
      if (filter !== 'all' && activity.action !== filter) {
        return false;
      }
      
      // Filter by search term
      if (searchTerm) {
        const searchString = JSON.stringify(activity).toLowerCase();
        return searchString.includes(searchTerm.toLowerCase());
      }
      
      return true;
    });
    
    if (groupByPersona) {
      // Group by persona
      const groups: Record<string, AgentActivity[]> = {};
      
      filteredActivities.forEach(activity => {
        const personaId = activity.persona_id || activity.agent_id;
        if (!groups[personaId]) {
          groups[personaId] = [];
        }
        groups[personaId].push(activity);
      });
      
      // Sort activities within each group by timestamp
      Object.keys(groups).forEach(personaId => {
        groups[personaId].sort((a, b) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
      });
      
      return groups;
    } else {
      // Just sort chronologically
      const sorted = [...filteredActivities].sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      // Return as a single group with a special key
      return { 'chronological': sorted };
    }
  };
  
  const groupedActivities = processActivities();
  
  // Calculate Gantt chart data
  const ganttData = useMemo(() => {
    if (activities.length === 0) return { timelineStart: 0, timelineEnd: 0, personaTimelines: [] };
    
    // Find the earliest and latest timestamps
    let earliestTime = Number.MAX_SAFE_INTEGER;
    let latestTime = 0;
    
    activities.forEach(activity => {
      const startTime = new Date(activity.timestamp).getTime();
      earliestTime = Math.min(earliestTime, startTime);
      
      // For the end time, use the activity's end_time if available
      // Otherwise, use the timestamp (don't extend to "now")
      const endTime = activity.end_time 
        ? new Date(activity.end_time).getTime() 
        : startTime;
      
      latestTime = Math.max(latestTime, endTime);
    });
    
    // Add some padding to the timeline
    earliestTime -= 60000; // 1 minute before
    latestTime += 60000;   // 1 minute after
    
    // Group activities by persona for the Gantt chart
    const personaGroups: Record<string, any[]> = {};
    
    activities.forEach(activity => {
      const personaId = activity.persona_id || activity.agent_id;
      if (!personaGroups[personaId]) {
        personaGroups[personaId] = [];
      }
      
      const startTime = new Date(activity.timestamp).getTime();
      const endTime = activity.end_time 
        ? new Date(activity.end_time).getTime() 
        : (activity.action === 'complete' ? startTime + 60000 : Date.now());
      
      // Calculate position and width as percentages
      const startPosition = ((startTime - earliestTime) / (latestTime - earliestTime)) * 100;
      const width = ((endTime - startTime) / (latestTime - earliestTime)) * 100;
      
      // Ensure minimum width for visibility but don't let it dominate
      const adjustedWidth = Math.max(2, Math.min(width, 80));
      
      personaGroups[personaId].push({
        ...activity,
        startPosition: Math.max(0, startPosition),
        width: adjustedWidth,
        startTime,
        endTime
      });
    });
    
    // Convert to array and sort by persona name
    const personaTimelines = Object.entries(personaGroups).map(([personaId, activities]) => ({
      personaId,
      personaName: getDisplayName(personaId),
      activities: activities.sort((a, b) => a.startTime - b.startTime)
    })).sort((a, b) => a.personaName.localeCompare(b.personaName));
    
    return {
      timelineStart: earliestTime,
      timelineEnd: latestTime,
      personaTimelines
    };
  }, [activities, getDisplayName]);
  
  // Get color for Gantt bar based on activity type
  const getGanttBarColor = (activity: AgentActivity) => {
    switch (activity.action) {
      case 'start':
        return darkMode ? 'bg-green-700' : 'bg-green-500';
      case 'thinking':
        return darkMode ? 'bg-yellow-700' : 'bg-yellow-500';
      case 'tool_usage':
        return darkMode ? 'bg-orange-700' : 'bg-orange-500';
      case 'message':
        return darkMode ? 'bg-blue-700' : 'bg-blue-500';
      case 'complete':
        return darkMode ? 'bg-green-800' : 'bg-green-600';
      case 'error':
        return darkMode ? 'bg-red-700' : 'bg-red-500';
      case 'artifact_created':
        return darkMode ? 'bg-purple-700' : 'bg-purple-500';
      default:
        return darkMode ? 'bg-gray-700' : 'bg-gray-500';
    }
  };
  
  // Render the Gantt chart view
  const renderGanttView = () => {
    if (activities.length === 0) {
      return (
        <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No agent activities recorded yet
        </div>
      );
    }
    
    // Format time for the timeline
    const formatTimeLabel = (timestamp: number) => {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    
    return (
      <div className="gantt-timeline">
        {/* Timeline header with time markers */}
        <div className="flex justify-between mb-1">
          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {formatTimeLabel(ganttData.timelineStart)}
          </div>
          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {formatTimeLabel(ganttData.timelineEnd)}
          </div>
        </div>
        
        {/* Timeline ruler */}
        <div className={`h-1 mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full`}>
          <div className={`h-full ${darkMode ? 'bg-blue-600' : 'bg-blue-500'} rounded-full`} style={{ width: '100%' }}></div>
        </div>
        
        {/* Persona timelines */}
        <div className="space-y-6">
          {ganttData.personaTimelines.map(personaTimeline => (
            <div key={personaTimeline.personaId} className="mb-4">
              <div className="flex items-center mb-2">
                <div className={`w-6 h-6 rounded-full ${darkMode ? 'bg-blue-600' : 'bg-blue-500'} flex items-center justify-center text-white mr-2`}>
                  {personaTimeline.personaName.charAt(0)}
                </div>
                <h4 className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  {personaTimeline.personaName}
                </h4>
                <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  ({personaTimeline.activities.length} activities)
                </span>
              </div>
              
              {/* Timeline container */}
              <div className={`${darkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg overflow-hidden p-4`}>
                {/* Timeline header with time markers */}
                <div className="flex justify-between mb-2">
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatTimeLabel(ganttData.timelineStart)}
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatTimeLabel(ganttData.timelineEnd)}
                  </div>
                </div>
                
                {/* Timeline ruler with tick marks */}
                <div className="h-6 relative mb-4">
                  {/* Background line */}
                  <div className={`absolute left-0 right-0 top-1/2 h-0.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}></div>
                  
                  {/* Tick marks */}
                  {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(tick => (
                    <div key={tick} className="absolute top-0 bottom-0" style={{ left: `${tick}%` }}>
                      <div className={`h-full w-0.5 ${darkMode ? 'bg-gray-600' : 'bg-gray-400'}`}></div>
                      <div className={`absolute -bottom-5 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} style={{ transform: 'translateX(-50%)' }}>
                        {tick === 0 ? formatTimeLabel(ganttData.timelineStart) : 
                         tick === 100 ? formatTimeLabel(ganttData.timelineEnd) : 
                         tick % 25 === 0 ? formatTimeLabel(ganttData.timelineStart + (ganttData.timelineEnd - ganttData.timelineStart) * (tick / 100)) : ''}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Activity timeline */}
                <div className="relative h-16 mb-4">
                  {/* Activity markers */}
                  {personaTimeline.activities.map((activity, index) => (
                    <div 
                      key={activity.id || index}
                      className="absolute"
                      style={{ 
                        left: `${activity.startPosition}%`,
                        top: 0,
                        bottom: 0
                      }}
                    >
                      {/* Vertical line */}
                      <div className={`absolute h-full w-0.5 ${
                        activity.action === 'start' ? 'bg-green-500' :
                        activity.action === 'thinking' ? 'bg-yellow-500' :
                        activity.action === 'complete' ? 'bg-green-600' :
                        activity.action === 'tool_usage' ? 'bg-orange-500' :
                        activity.action === 'message' ? 'bg-blue-500' :
                        activity.action === 'error' ? 'bg-red-500' :
                        activity.action === 'artifact_created' ? 'bg-purple-500' :
                        'bg-gray-500'
                      }`}></div>
                      
                      {/* Activity dot */}
                      <div 
                        className={`absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full cursor-pointer ${getGanttBarColor(activity)}`}
                        onClick={() => setExpandedActivity(expandedActivity === activity.id ? null : activity.id)}
                        title={`${getActivityTitle(activity)} (${formatTimestamp(activity.timestamp)})`}
                      ></div>
                      
                      {/* Activity label */}
                      <div 
                        className={`absolute whitespace-nowrap text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                        style={{ 
                          top: index % 2 === 0 ? '-20px' : '20px',
                          transform: 'translateX(-50%)'
                        }}
                      >
                        {activity.action}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Activity list below timeline */}
                <div className="mt-8 space-y-2">
                  <h5 className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Activity Details</h5>
                  
                  {personaTimeline.activities.map((activity, index) => (
                    <div 
                      key={`list-${activity.id || index}`}
                      className={`p-2 rounded cursor-pointer ${
                        expandedActivity === activity.id 
                          ? darkMode ? 'bg-gray-700 border-l-4 border-blue-500' : 'bg-white border-l-4 border-blue-500' 
                          : darkMode ? 'bg-gray-700 hover:bg-gray-650' : 'bg-white hover:bg-gray-50'
                      }`}
                      onClick={() => setExpandedActivity(expandedActivity === activity.id ? null : activity.id)}
                    >
                      <div className="flex items-start">
                        <div className="mr-3 mt-1">
                          {getActivityIcon(activity)}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <div className="font-medium text-sm">
                              {getActivityTitle(activity)}
                            </div>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {formatTimestamp(activity.timestamp)}
                            </div>
                          </div>
                          
                          {/* Expanded details */}
                          {expandedActivity === activity.id && (
                            <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                              {/* Message content */}
                              {activity.details?.message && (
                                <div className="mb-3">
                                  <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Message:
                                  </div>
                                  <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} p-2 rounded ${
                                    darkMode ? 'bg-gray-800' : 'bg-gray-100'
                                  }`}>
                                    {activity.details.message}
                                  </div>
                                </div>
                              )}
                              
                              {/* Raw details */}
                              <div className="mt-3">
                                <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Raw Details:
                                </div>
                                <pre className={`text-xs overflow-auto p-2 rounded ${
                                  darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {JSON.stringify(activity.details, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Expanded activity details */}
              {personaTimeline.activities.map(activity => (
                expandedActivity === activity.id && (
                  <div 
                    key={`detail-${activity.id}`}
                    className={`mt-2 p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
                  >
                    <div className="flex items-start">
                      <div className="mr-3 mt-1">
                        {getActivityIcon(activity)}
                      </div>
                      <div>
                        <div className="font-medium">
                          {getActivityTitle(activity)}
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                          Started: {formatTimestamp(activity.timestamp)}
                        </div>
                        {activity.end_time && (
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Ended: {formatTimestamp(activity.end_time)}
                          </div>
                        )}
                        {activity.duration && (
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Duration: {formatDuration(activity.duration)}
                          </div>
                        )}
                        {activity.phase_label && (
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                            Phase: {activity.phase_label}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Activity details */}
                    <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      {/* Message content */}
                      {activity.details?.message && (
                        <div className="mb-3">
                          <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Message:
                          </div>
                          <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} p-2 rounded ${
                            darkMode ? 'bg-gray-800' : 'bg-gray-100'
                          }`}>
                            {activity.details.message}
                          </div>
                        </div>
                      )}
                      
                      {/* Thought content */}
                      {activity.details?.thought && (
                        <div className="mb-3">
                          <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Thought Process:
                          </div>
                          <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} p-2 rounded ${
                            darkMode ? 'bg-gray-800' : 'bg-gray-100'
                          }`}>
                            {activity.details.thought}
                          </div>
                        </div>
                      )}
                      
                      {/* Raw details */}
                      <div className="mt-3">
                        <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Raw Details:
                        </div>
                        <pre className={`text-xs overflow-auto p-2 rounded ${
                          darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {JSON.stringify(activity.details, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                )
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="agent-activity-timeline">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className={`text-lg font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          Agent Activity Timeline
        </h3>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* View mode toggle */}
          <div className="flex border rounded overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`px-2 py-1 flex items-center ${
                viewMode === 'list' 
                  ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                  : darkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}
              title="List view"
            >
              <FiList size={14} className="mr-1" /> List
            </button>
            <button
              onClick={() => setViewMode('gantt')}
              className={`px-2 py-1 flex items-center ${
                viewMode === 'gantt' 
                  ? darkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                  : darkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}
              title="Gantt view"
            >
              <FiBarChart2 size={14} className="mr-1" /> Timeline
            </button>
          </div>
          {/* Group toggle */}
          <label className={`flex items-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <input
              type="checkbox"
              checked={groupByPersona}
              onChange={() => setGroupByPersona(!groupByPersona)}
              className="mr-2"
            />
            Group by persona
          </label>
          
          {/* Filter dropdown */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={`px-2 py-1 text-sm rounded border ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'
            }`}
          >
            <option value="all">All Activities</option>
            <option value="start">Start</option>
            <option value="thinking">Thinking</option>
            <option value="tool_usage">Tool Usage</option>
            <option value="message">Messages</option>
            <option value="complete">Completed</option>
            <option value="error">Errors</option>
            <option value="artifact_created">Artifacts</option>
          </select>
          
          {/* Search input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-8 pr-2 py-1 text-sm rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'
              }`}
            />
            <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
          </div>
        </div>
      </div>
      
      {/* Render either list view or Gantt view based on viewMode */}
      {viewMode === 'gantt' ? (
        renderGanttView()
      ) : (
        Object.keys(groupedActivities).length === 0 ? (
          <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No activities match your filters
          </div>
        ) : (
        Object.entries(groupedActivities).map(([personaId, personaActivities]) => (
          <div key={personaId} className={`mb-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-50'} p-4`}>
            {/* Only show persona header if grouped by persona */}
            {groupByPersona && (
              <h4 className="font-medium mb-3 flex items-center">
                <div className={`w-6 h-6 rounded-full ${darkMode ? 'bg-blue-600' : 'bg-blue-500'} flex items-center justify-center text-white mr-2`}>
                  {getDisplayName(personaId).charAt(0)}
                </div>
                {getDisplayName(personaId)}
                <span className="ml-2 text-xs text-gray-500">
                  ({personaActivities.length} activities)
                </span>
              </h4>
            )}
            
            <div className="relative">
              {/* Timeline line */}
              <div className={`absolute left-4 top-0 bottom-0 w-0.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
              
              {/* Activities */}
              <div className="space-y-3">
                {personaActivities.map((activity, activityIndex) => (
                  <div 
                    key={`${personaId}-${activity.id || activityIndex}`} 
                    className="relative pl-10"
                  >
                    {/* Timeline dot with appropriate color */}
                    <div className={`absolute left-4 top-1.5 w-2 h-2 rounded-full transform -translate-x-1/2 ${
                      activity.action === 'start' ? 'bg-green-500' :
                      activity.action === 'thinking' ? 'bg-yellow-500' :
                      activity.action === 'complete' ? 'bg-green-500' :
                      activity.action === 'tool_usage' ? 'bg-orange-500' :
                      activity.action === 'message' ? 'bg-blue-500' :
                      activity.action === 'error' ? 'bg-red-500' :
                      activity.action === 'artifact_created' ? 'bg-purple-500' :
                      'bg-gray-500'
                    }`}></div>
                    
                    {/* Activity card */}
                    <div 
                      className={`p-3 rounded-lg cursor-pointer shadow-sm ${
                        getActivityBackground(activity)
                      } hover:${
                        getActivityBackground(activity, true)
                      } transition-colors`}
                      onClick={() => setExpandedActivity(expandedActivity === activity.id ? null : activity.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-start">
                          <div className="mr-3 mt-1">
                            {getActivityIcon(activity)}
                          </div>
                          <div>
                            <div className="font-medium">
                              {getActivityTitle(activity)}
                            </div>
                            
                            {/* Show phase info if not grouped by persona */}
                            {!groupByPersona && activity.phase_label && (
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                                Phase: {activity.phase_label}
                              </div>
                            )}
                            
                            {/* Show persona info if not grouped by persona */}
                            {!groupByPersona && (
                              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                                Persona: {getDisplayName(activity.persona_id || activity.agent_id)}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end">
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {formatTimestamp(activity.timestamp)}
                          </div>
                          
                          {activity.duration && (
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                              Duration: {formatDuration(activity.duration)}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Expanded details */}
                      {expandedActivity === activity.id && (
                        <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                          {/* Message content */}
                          {activity.details?.message && (
                            <div className="mb-3">
                              <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Message:
                              </div>
                              <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} p-2 rounded ${
                                darkMode ? 'bg-gray-800' : 'bg-gray-100'
                              }`}>
                                {activity.details.message}
                              </div>
                            </div>
                          )}
                          
                          {/* Thought content */}
                          {activity.details?.thought && (
                            <div className="mb-3">
                              <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Thought Process:
                              </div>
                              <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} p-2 rounded ${
                                darkMode ? 'bg-gray-800' : 'bg-gray-100'
                              }`}>
                                {activity.details.thought}
                              </div>
                            </div>
                          )}
                          
                          {/* Tool details */}
                          {activity.action === 'tool_usage' && (
                            <div className="mb-3">
                              <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Tool Details:
                              </div>
                              <div className={`grid grid-cols-2 gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                <div className={`p-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                  <span className="font-medium">Name:</span> {activity.details?.tool_name || 'Unknown'}
                                </div>
                                {activity.details?.tool_input && (
                                  <div className={`p-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                                    <span className="font-medium">Input:</span> {
                                      typeof activity.details.tool_input === 'string' 
                                        ? activity.details.tool_input 
                                        : JSON.stringify(activity.details.tool_input)
                                    }
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Artifact details */}
                          {activity.details?.artifact_id && (
                            <div className="mb-3">
                              <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Related Artifact:
                              </div>
                              <div className={`p-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} flex items-center`}>
                                <FiFileText className="mr-2 text-purple-500" />
                                <span className="text-sm">
                                  {activity.details.artifact_name || activity.details.artifact_id}
                                </span>
                              </div>
                            </div>
                          )}
                          
                          {/* Phase info */}
                          {activity.phase_label && (
                            <div className="mb-3">
                              <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Phase:
                              </div>
                              <div className={`p-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} text-sm`}>
                                {activity.phase_label}
                              </div>
                            </div>
                          )}
                          
                          {/* Raw details (automatically expanded) */}
                          <div className="mt-3">
                            <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Raw Details:
                            </div>
                            <pre className={`text-xs overflow-auto p-2 rounded ${
                              darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {JSON.stringify(activity.details, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))
        )
      )}
    </div>
  );
};
